import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Router} from 'react-router-dom';

import AppLoaderRaw from '@/components/Loader/AppLoader/AppLoaderRaw';
import ErrorBoundary from '@/containers/ErrorBoundary/ErrorBoundary';
import Routes from '@/containers/Routes/Routes';
import useDeviceDetect from '@/hooks/device/useDeviceDetect';
import useHolderDetect from '@/hooks/device/useHolderDetect';
import useIotWebSocketReconnect from '@/hooks/device/useIotWebSocketReconnect';
import useFwuChecker from '@/hooks/fwu/useFwuChecker';
import useGaPageTracking from '@/hooks/ga/useGaPageTracking';
import useGoogleAnalytics from '@/hooks/ga/useGoogleAnalytics';
import useLayoutChange from '@/hooks/layout/useLayoutChange';
import useCheckBadgeEarn from '@/hooks/loyalty/useCheckBadgeEarn';
import useExpiringBadge from '@/hooks/loyalty/useExpiringBadge';
import useJourneyLevelTaskComplete from '@/hooks/loyalty/useJourneyLevelTaskComplete';
import useLoyaltyFetchManager from '@/hooks/loyalty/useLoyaltyFetchManager';
import {useLoyaltyPopupCoordinator} from '@/hooks/loyalty/useLoyaltyPopupCoordinator';
import usePushNotificationsPrompt from '@/hooks/pushNotifications/usePushNotificationsPrompt';
import useAppSsoCookieRefresh from '@/hooks/sso/useAppSsoCookieRefresh';
import useAppInstalled from '@/hooks/useAppInstalled';
import useDeviceTypeLogger from '@/hooks/useDeviceTypeLogger';
import useFeedbackPopup from '@/hooks/useFeedbackPopup';
import useIqosMw from '@/hooks/useIqosMw';
import useOnReturnActionManager from '@/hooks/useOnReturnActionManager';
import useOnReturnFromWebAppConnection from '@/hooks/useOnReturnFromWebAppConnection';
import usePushNotificationSubscriptionManager from '@/hooks/usePushNotificationSubscriptionManager';
import useSWMessage from '@/hooks/useSWMessage';
import useSWMessageHandler from '@/hooks/useSWMessageHandler';
import appService from '@/services/appService';
import history from '@/services/history';
import log from '@/services/logger/log';
import {ensureOneTrustBannerScriptInjected} from '@/services/oneTrust/oneTrustBannerBootstrap';
import appRouterService from '@/services/route/appRouterService';
import userBrowserService from '@/services/user/userBrowserService';
import {selectIsAem, selectIsAppLoading} from '@/state/selectors/global';
import {setIsAppLoaded} from '@/state/slices/globalSlice';

import AppUpdatePopup from './components/AppUpdatePopup/AppUpdatePopup';
import useAmplitudeInit from './hooks/amplitude/useAmplitudeInit';
import useConsumerSettingsChecker from './hooks/useConsumerSettingsChecker';
import useFetchConsumerProducts from './hooks/useFetchConsumerProducts';
import useInitSelectedDeviceCodentify from './hooks/useInitSelectedDeviceCodentify';
import useUpdateBadges from './hooks/useUpdateBadges';

const App = () => {
    const isAppLoading = useSelector(selectIsAppLoading);
    const isAem = useSelector(selectIsAem);
    const dispatch = useDispatch();

    const onInit = async () => {
        const isInStandaloneMode = userBrowserService.isInStandaloneMode();

        log.info(`appService: app is initialized, ua: ${userBrowserService.getUserAgent()}`);
        log.info(`App type: ${isInStandaloneMode ? 'PWA' : 'In-Browser'}`);
        ensureOneTrustBannerScriptInjected();

        if (!isAem) {
            appRouterService.forwardToRootPage();
        }
        await appService.onAppStart();

        dispatch(setIsAppLoaded());
        log.info(`App: app is started successfully`);
    };

    useEffect(() => {
        onInit();
    }, []);

    useIqosMw();
    useAppSsoCookieRefresh();
    useDeviceTypeLogger();
    useAppInstalled();
    useSWMessage();
    useSWMessageHandler();
    useUpdateBadges();
    useAmplitudeInit();
    useGoogleAnalytics();
    useFeedbackPopup();
    useFetchConsumerProducts();
    useConsumerSettingsChecker();
    useInitSelectedDeviceCodentify();
    useFwuChecker();
    useHolderDetect();
    useDeviceDetect();
    useIotWebSocketReconnect();
    usePushNotificationsPrompt();
    usePushNotificationSubscriptionManager();
    useLoyaltyFetchManager();
    useOnReturnActionManager();
    useCheckBadgeEarn();
    useJourneyLevelTaskComplete();
    useLoyaltyPopupCoordinator();
    useOnReturnFromWebAppConnection();
    useExpiringBadge();

    return (
        <ErrorBoundary>
            <LayoutChangeListener />
            <AppUpdatePopup />
            {isAppLoading ? (
                <AppLoaderRaw />
            ) : (
                <Router history={history.getHistory()}>
                    <GoogleAnalyticsPageTracking />
                    <Routes />
                </Router>
            )}
        </ErrorBoundary>
    );
};

export default App;

const LayoutChangeListener = () => {
    useLayoutChange();
};

const GoogleAnalyticsPageTracking = () => {
    useGaPageTracking();
};
